import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  CheckCircle,
  Clock,
  ArrowRight,
  ChevronRight,
  Shield,
  Users,
  Star,
  Zap,
  ShoppingCart,
  User,
  Percent,
  CalendarDays,
  Flame,
  Armchair,
  Lock,
  Leaf,
  Sparkles,
} from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

const WELLNESS_HERO =
  'https://static.wixstatic.com/media/133859_430295303fe04dd3b57440c0d29d1183~mv2.jpg/v1/fill/w_1920,h_1080,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/20231225_082202_edited_edited.jpg';

const SUITE_ROOM_IMG = 'https://customer-assets-eiarnc6j.emergentagent.net/job_9201cd4d-3237-462f-8c6c-dda1d91aaa6b/artifacts/8piux3h3_1000028139.png';

const PERKS = [
  {
    icon: ShoppingCart,
    title: 'Pre-Order Products',
    desc: 'Pre-order your supplements & vitamins online for effortless in-store pick up. Skip the wait, grab and go.',
  },
  {
    icon: User,
    title: 'Personalized Vitamin Plan',
    desc: 'Receive a custom vitamin and supplement plan tailored to your health goals by our certified practitioners.',
  },
  {
    icon: Percent,
    title: 'Earn 5% Cashback',
    desc: 'Every session earns you 5% cashback in store rewards — redeemable on vitamins, supplements, and personal care.',
  },
  {
    icon: CalendarDays,
    title: '10% Off Every Wednesday',
    desc: 'Members enjoy an exclusive 10% discount on all in-store purchases every Wednesday. Wellness just got smarter.',
  },
];

const EQUIPMENT = [
  {
    icon: Flame,
    name: 'Infrared Red Light Therapy',
    desc: 'Our state-of-the-art infrared pod uses targeted red light to reduce inflammation, boost circulation, and accelerate recovery.',
  },
  {
    icon: Armchair,
    name: '4D Deep Tissue Massage Chair',
    desc: 'Experience full-body decompression with our premium 4D deep tissue zero-gravity massage chair — targeting muscles at multiple depths to melt away tension in minutes.',
  },
  {
    icon: Lock,
    name: 'Fully Private Sessions',
    desc: 'Your session is 100% private. No shared spaces, no interruptions — just you and your recovery time.',
  },
];

const TIERS = [
  {
    id: 'single',
    name: 'Single Session',
    price: '$69',
    period: 'for 1 hour',
    description: 'Perfect for first-timers or occasional visits. One full hour in your private wellness suite.',
    features: [
      '1 private session · 1 full hour',
      'Infrared red light therapy access',
      '4D deep tissue massage chair',
      'Complimentary health intake',
    ],
    cta: 'Book Now',
    highlighted: false,
    badge: null,
  },
  {
    id: '2_pack',
    name: '2-Session Pack',
    price: '$125',
    period: 'use in 30 days',
    description: 'Save $13 and lock in two private hour-long sessions. Valid for 30 days from purchase.',
    features: [
      '2 private sessions · 1 hour each',
      'Infrared red light + 4D massage chair',
      'Save $13 vs. single sessions',
      'Flexible scheduling',
    ],
    cta: 'Get the Pack',
    highlighted: false,
    badge: 'Popular',
    savings: 'Save $13',
  },
  {
    id: 'membership',
    name: 'Monthly Membership',
    price: '$220',
    period: '/month',
    description: '4 private sessions per month + all Wellness Suite Perks — pre-order, vitamin plan, 5% cashback, and Wednesday 10% off.',
    features: [
      '4 sessions/month · 1 hour each',
      'Priority booking access',
      'All Wellness Suite Perks included',
      '5% cashback in store rewards',
      '10% off every Wednesday',
      'Personalized vitamin plan',
    ],
    cta: 'Join the Club',
    highlighted: true,
    badge: 'Best Value',
    savings: 'Save $56/mo',
  },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Choose your plan',
    desc: 'Select a single session, 2-pack, or monthly membership below. All plans include a personalized experience.',
  },
  {
    step: '02',
    title: 'Complete your intake form',
    desc: 'A brief health & wellness intake form is required before your first session to ensure your safety and best results.',
  },
  {
    step: '03',
    title: 'Book your time',
    desc: 'Pick a date and time that works for you from our real-time availability. Members enjoy priority access.',
  },
  {
    step: '04',
    title: 'Experience the difference',
    desc: 'Arrive at 6130 Medau Pl in Montclair Village. Our certified staff will guide you through your session.',
  },
];

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { el.classList.add('revealed'); obs.disconnect(); }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

export default function WellnessClubPage() {
  const howRef = useReveal();
  const pricingRef = useReveal();
  const faqRef = useReveal();
  const equipRef = useReveal();
  const perksRef = useReveal();

  return (
    <div className="min-h-screen bg-[#F3F4F6] font-['Manrope']">
      <style>{`
        .reveal-section { opacity: 0; transform: translateY(28px); transition: opacity 0.7s ease, transform 0.7s ease; }
        .reveal-section.revealed { opacity: 1; transform: none; }
        .stagger-child { opacity: 0; transform: translateY(20px); transition: opacity 0.6s ease, transform 0.6s ease; }
        .revealed .stagger-child:nth-child(1) { opacity:1; transform:none; transition-delay:0.05s; }
        .revealed .stagger-child:nth-child(2) { opacity:1; transform:none; transition-delay:0.15s; }
        .revealed .stagger-child:nth-child(3) { opacity:1; transform:none; transition-delay:0.25s; }
        .revealed .stagger-child:nth-child(4) { opacity:1; transform:none; transition-delay:0.35s; }
        .revealed .stagger-child:nth-child(5) { opacity:1; transform:none; transition-delay:0.45s; }
      `}</style>

      <Navbar />

      {/* ── HERO ── */}
      <section
        data-testid="wellness-hero"
        className="relative min-h-[70vh] flex items-end overflow-hidden pt-20"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${WELLNESS_HERO}")` }}
          role="img"
          aria-label="Healthy Life Vitamins Wellness Suite"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 w-full">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
            className="max-w-2xl"
          >
            <motion.div
              variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
              className="inline-flex items-center gap-2 bg-[#d97736]/90 text-white text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-5"
            >
              <Zap size={11} />
              Wellness Suite
            </motion.div>

            <motion.h1
              variants={{ hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } }}
              className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl lg:text-7xl font-semibold text-white leading-tight mb-3"
            >
              Your time.<br />Your recovery.
            </motion.h1>

            <motion.p
              variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
              className="text-[#d97736] font-bold uppercase tracking-[0.2em] text-sm mb-5"
            >
              Recover. Recharge. Repeat.
            </motion.p>

            <motion.p
              variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
              className="text-white/85 text-base sm:text-lg leading-relaxed mb-8 max-w-lg"
            >
              Book personalized wellness sessions at Healthy Life Vitamins in Montclair Village. Choose a single session, a 2-pack, or join as a monthly member.
            </motion.p>

            <motion.div
              variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } }}
              className="flex flex-col sm:flex-row gap-3"
            >
              <a
                href="/booking"
                data-testid="wellness-hero-book-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#d97736] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#b8662a] transition-all hover:-translate-y-px shadow-lg text-sm"
              >
                Book a Session Now
                <ArrowRight size={15} />
              </a>
              <a
                href="/account"
                data-testid="wellness-hero-account-cta"
                className="inline-flex items-center justify-center gap-2 bg-white/15 backdrop-blur-sm text-white font-semibold px-7 py-3.5 rounded-full border border-white/30 hover:bg-white/25 transition-all text-sm"
              >
                <Users size={15} />
                Check My Credits
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── EQUIPMENT / EXPERIENCE ── */}
      <section
        data-testid="equipment-section"
        className="bg-[#0e2418] text-white overflow-hidden"
      >
        <div className="max-w-7xl mx-auto">
          <div ref={equipRef} className="reveal-section grid grid-cols-1 lg:grid-cols-2 min-h-[520px]">

            {/* Left — suite room photo */}
            <div className="relative min-h-[320px] lg:min-h-0 order-2 lg:order-1">
              <img
                src={SUITE_ROOM_IMG}
                alt="Healthy Life Vitamins Wellness Suite — infrared red light therapy pod and zero-gravity massage chair"
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="lazy"
              />
              {/* Bottom strip */}
              <div className="absolute bottom-0 inset-x-0 bg-black/60 backdrop-blur-sm px-6 py-4 grid grid-cols-3 text-center gap-4">
                {[
                  { icon: Lock, label: 'Private Session' },
                  { icon: Leaf, label: 'Relax & Restore' },
                  { icon: Sparkles, label: 'Feel Your Best' },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex flex-col items-center gap-1.5">
                    <Icon size={20} className="text-[#d97736]" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/80">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — equipment list */}
            <div className="order-1 lg:order-2 flex flex-col justify-center px-8 sm:px-12 lg:px-16 py-16 stagger-child">
              <p className="text-[#d97736] text-xs font-bold uppercase tracking-[0.25em] mb-4">
                What's Inside
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-semibold mb-2 leading-tight">
                Recover.<br />Recharge.<br />Repeat.
              </h2>
              <p className="text-white/60 text-sm uppercase tracking-[0.18em] font-semibold mb-10">
                Your time. Your recovery.
              </p>

              <div className="space-y-8">
                {EQUIPMENT.map((item) => (
                  <div key={item.name} className="flex gap-5 items-start group">
                    <div className="w-11 h-11 rounded-2xl bg-[#d97736]/15 border border-[#d97736]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#d97736]/25 transition-colors">
                      <item.icon size={20} className="text-[#d97736]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">{item.name}</h3>
                      <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <a
                  href="/booking"
                  className="inline-flex items-center gap-2 bg-[#d97736] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#b8662a] transition-all text-sm hover:-translate-y-px"
                >
                  Book Your Session
                  <ArrowRight size={15} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="bg-white py-20 sm:py-28" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={howRef} className="reveal-section">
            <div className="text-center mb-14 stagger-child">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#d97736] mb-3">
                Simple Process
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-semibold text-[#1f3d2b]">
                How it works
              </h2>
            </div>

            <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Single centered connector line across all 4 steps */}
              <div className="hidden lg:block absolute top-6 left-[12.5%] right-[12.5%] h-px bg-[#1f3d2b]/20" />

              {HOW_IT_WORKS.map((step, i) => (
                <div
                  key={step.step}
                  data-testid={`how-step-${i}`}
                  className="stagger-child flex flex-col items-center text-center"
                >
                  {/* Icon — white ring masks the connector line cleanly */}
                  <div className="relative z-10 w-12 h-12 rounded-2xl bg-[#1f3d2b]/8 flex items-center justify-center mb-5 ring-4 ring-white">
                    <span className="text-lg font-bold font-['Cormorant_Garamond'] text-[#1f3d2b]">
                      {step.step}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section
        data-testid="pricing-section"
        className="bg-[#F3F4F6] py-20 sm:py-28"
        id="pricing"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={pricingRef} className="reveal-section">
            <div className="text-center mb-14 stagger-child">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#d97736] mb-3">
                Transparent Pricing
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-semibold text-[#1f3d2b] mb-4">
                Choose your plan
              </h2>
              <p className="text-gray-500 text-base max-w-lg mx-auto">
                All sessions are at 6130 Medau Pl, Montclair Village, Oakland. Secure checkout via Stripe.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {TIERS.map((tier) => (
                <div
                  key={tier.id}
                  data-testid={`pricing-${tier.id}`}
                  className={`stagger-child relative rounded-3xl flex flex-col overflow-hidden transition-shadow duration-500 ${
                    tier.highlighted
                      ? 'bg-[#1f3d2b] text-white shadow-2xl ring-2 ring-[#1f3d2b] -translate-y-2'
                      : 'bg-white text-gray-800 shadow-sm hover:shadow-xl border border-gray-100'
                  }`}
                >
                  {/* Badge */}
                  {tier.badge && (
                    <div className={`absolute top-5 right-5 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${
                      tier.highlighted ? 'bg-[#d97736] text-white' : 'bg-[#1f3d2b]/10 text-[#1f3d2b]'
                    }`}>
                      {tier.badge}
                    </div>
                  )}

                  <div className="p-8 sm:p-9 flex flex-col flex-1">
                    {/* Plan name */}
                    <h3 className={`font-['Cormorant_Garamond'] text-2xl font-semibold mb-1 ${
                      tier.highlighted ? 'text-white' : 'text-[#1f3d2b]'
                    }`}>
                      {tier.name}
                    </h3>

                    {/* Price */}
                    <div className="flex items-end gap-1 my-4">
                      <span className={`text-4xl font-bold font-['Cormorant_Garamond'] ${
                        tier.highlighted ? 'text-white' : 'text-gray-900'
                      }`}>
                        {tier.price}
                      </span>
                      <span className={`text-sm pb-1 ${tier.highlighted ? 'text-white/70' : 'text-gray-500'}`}>
                        {tier.period}
                      </span>
                    </div>

                    {/* Savings badge */}
                    {tier.savings && (
                      <div className={`inline-flex self-start text-xs font-bold px-2.5 py-1 rounded-full mb-4 ${
                        tier.highlighted ? 'bg-white/15 text-white' : 'bg-[#d97736]/10 text-[#d97736]'
                      }`}>
                        {tier.savings}
                      </div>
                    )}

                    <p className={`text-sm leading-relaxed mb-6 ${
                      tier.highlighted ? 'text-white/75' : 'text-gray-500'
                    }`}>
                      {tier.description}
                    </p>

                    {/* Features */}
                    <ul className="space-y-2.5 flex-1 mb-8">
                      {tier.features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5 text-sm">
                          <CheckCircle
                            size={15}
                            className={`shrink-0 mt-0.5 ${tier.highlighted ? 'text-[#d97736]' : 'text-[#1f3d2b]'}`}
                          />
                          <span className={tier.highlighted ? 'text-white/85' : 'text-gray-700'}>
                            {f}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <a
                      href="/booking"
                      data-testid={`pricing-cta-${tier.id}`}
                      className={`block w-full text-center font-semibold py-3.5 rounded-full text-sm transition-all hover:-translate-y-px ${
                        tier.highlighted
                          ? 'bg-[#d97736] text-white hover:bg-[#b8662a] shadow-lg'
                          : 'bg-[#1f3d2b] text-white hover:bg-[#2d5a3e]'
                      }`}
                    >
                      {tier.cta}
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust signals */}
            <div className="mt-12 flex flex-wrap justify-center gap-6 text-sm text-gray-500 stagger-child">
              {[
                { icon: Shield, text: 'Secure checkout via Stripe' },
                { icon: Clock, text: 'Flexible scheduling' },
                { icon: Star, text: 'Certified staff' },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2">
                  <Icon size={15} className="text-[#1f3d2b]" />
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WELLNESS SUITE PERKS ── */}
      <section
        data-testid="perks-section"
        className="bg-white py-20 sm:py-28"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={perksRef} className="reveal-section">

            {/* Header */}
            <div className="text-center mb-14 stagger-child">
              <div className="inline-flex items-center gap-2 bg-[#1f3d2b] text-white text-xs font-bold uppercase tracking-[0.22em] px-4 py-2 rounded-full mb-5">
                <Star size={11} className="fill-[#d97736] text-[#d97736]" />
                Wellness Club Perks
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-semibold text-[#1f3d2b] mb-4">
                Members get more
              </h2>
              <p className="text-gray-500 text-base max-w-lg mx-auto">
                Every monthly membership includes exclusive perks beyond your sessions — rewards, discounts, and personalized care built into your plan.
              </p>
            </div>

            {/* 4 perks grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {PERKS.map((perk, i) => (
                <div
                  key={perk.title}
                  data-testid={`perk-card-${i}`}
                  className="stagger-child group relative bg-[#F3F4F6] rounded-3xl p-7 hover:bg-[#1f3d2b] transition-all duration-500 hover:-translate-y-1 hover:shadow-xl"
                >
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-2xl bg-[#1f3d2b]/10 group-hover:bg-white/15 flex items-center justify-center mb-5 transition-colors">
                    <perk.icon size={22} className="text-[#1f3d2b] group-hover:text-[#d97736] transition-colors" />
                  </div>
                  <h3 className="font-semibold text-gray-900 group-hover:text-white text-base mb-2 transition-colors">
                    {perk.title}
                  </h3>
                  <p className="text-sm text-gray-500 group-hover:text-white/70 leading-relaxed transition-colors">
                    {perk.desc}
                  </p>

                  {/* Membership badge */}
                  <div className="mt-5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#d97736] group-hover:text-[#d97736]">
                    <CheckCircle size={11} />
                    Included with Membership
                  </div>
                </div>
              ))}
            </div>

            {/* Membership CTA strip */}
            <div className="mt-12 bg-[#1f3d2b] rounded-3xl p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6 stagger-child">
              <div>
                <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-1">Monthly Membership</p>
                <p className="font-['Cormorant_Garamond'] text-3xl font-semibold text-white">
                  All perks included · $220/month
                </p>
                <p className="text-white/65 text-sm mt-1">4 sessions + rewards + vitamin plan + Wednesday savings</p>
              </div>
              <a
                href="/booking"
                data-testid="perks-join-cta"
                className="flex-shrink-0 inline-flex items-center gap-2 bg-[#d97736] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#b8662a] transition-all text-sm hover:-translate-y-px whitespace-nowrap"
              >
                Join the Club
                <ArrowRight size={15} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── WAIVER NOTICE ── */}
      <section className="bg-white py-12 sm:py-16 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#d97736]/10 text-[#d97736] text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
            <Shield size={11} />
            Required for First-Time Visitors
          </div>
          <h3 className="font-['Cormorant_Garamond'] text-3xl font-semibold text-[#1f3d2b] mb-4">
            Health & Liability Intake Form
          </h3>
          <p className="text-gray-600 text-base leading-relaxed">
            All new Wellness Suite members must complete a brief health screening and liability acknowledgment before their first session. This protects both you and our staff, and takes less than 2 minutes to complete online. The form will appear automatically during your first booking.
          </p>
          <p className="text-gray-500 text-sm mt-4">
            Please remove all jewelry (rings, bracelets, earrings) before using wellness equipment.
          </p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section
        data-testid="faq-section"
        className="bg-[#F3F4F6] py-20 sm:py-28"
        id="faq"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={faqRef} className="reveal-section">
            <div className="text-center mb-12 stagger-child">
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-semibold text-[#1f3d2b]">
                Frequently asked
              </h2>
            </div>

            <div className="space-y-4 stagger-child">
              {[
                {
                  q: 'Do I need an account to book?',
                  a: 'You can book a single session without an account. For session packs and membership, a customer account is automatically created using the email you provide at checkout. You can log in at any time at /account to check your remaining credits.',
                },
                {
                  q: 'How do I use my session credits?',
                  a: 'Once you have an active pack or membership, visit the booking page and select "Use Credit." Your credits are tied to your phone number or email. Credits from packs expire 30 days after purchase; membership credits renew monthly.',
                },
                {
                  q: 'Can I cancel my monthly membership?',
                  a: 'Yes. You can cancel your membership at any time via your account page or by calling the store. Cancellation takes effect at the end of your current billing period.',
                },
                {
                  q: 'What should I do before my session?',
                  a: 'Please remove all jewelry (rings, bracelets, earrings) before using the equipment. Wear comfortable clothing. Arrive a few minutes early for your first visit to complete the health intake form if you haven\'t already done so online.',
                },
                {
                  q: 'Where are you located?',
                  a: '6130 Medau Pl, Oakland, CA 94611 — in the heart of Montclair Village. Free street parking is available. We\'re open Monday–Saturday 10AM–6PM and Sunday 11AM–5PM.',
                },
              ].map((item, i) => (
                <details
                  key={i}
                  className="bg-white rounded-2xl border border-gray-100 overflow-hidden group"
                  data-testid={`faq-item-${i}`}
                >
                  <summary className="flex items-center justify-between p-5 sm:p-6 cursor-pointer list-none font-semibold text-gray-900 hover:text-[#1f3d2b] transition-colors">
                    <span>{item.q}</span>
                    <ChevronRight size={18} className="text-gray-400 group-open:rotate-90 transition-transform shrink-0 ml-4" />
                  </summary>
                  <div className="px-5 sm:px-6 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-50">
                    <p className="pt-4">{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="bg-[#1f3d2b] py-20 sm:py-28 text-white text-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#d97736] mb-4">
            Ready to start?
          </p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-semibold mb-5">
            Book your first session today
          </h2>
          <p className="text-white/75 text-base mb-8">
            Join the Wellness Suite at Healthy Life Vitamins in Montclair Village. Your health journey starts here.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/booking"
              data-testid="wellness-final-book-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#d97736] text-white font-semibold px-8 py-4 rounded-full hover:bg-[#b8662a] transition-all hover:-translate-y-px shadow-lg"
            >
              Book a Session
              <ArrowRight size={16} />
            </a>
            <a
              href="/kiosk"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition-all"
            >
              Get Supplement Advice
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
